import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { useTopbar } from "@/components/chrome/use-topbar";

interface Market {
  id: string;
  name: string;
  state: string;
  msa: string | null;
  zipsCsv: string | null;
  loanBandMinUsd: number;
  loanBandMaxUsd: number;
  termMonthsMin: number;
  termMonthsMax: number;
  status: "active" | "draft" | "paused";
}

export const Route = createFileRoute("/_app/settings/markets/")({
  component: MarketsListPage,
});

function statusBadgeClass(status: Market["status"]): string {
  if (status === "active") return "bg-pos-bg text-pos";
  if (status === "paused") return "bg-warn-bg text-warn";
  return "bg-paper-3 text-muted";
}

function MarketsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery<Market[]>({
    queryKey: ["settings", "markets"],
    queryFn: () => apiFetch<Market[]>("/api/settings/markets"),
  });

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [state, setState] = useState("");
  const [msa, setMsa] = useState("");
  const [loanMin, setLoanMin] = useState("100000");
  const [loanMax, setLoanMax] = useState("1500000");
  const [termMin, setTermMin] = useState("6");
  const [termMax, setTermMax] = useState("24");
  const [status, setStatus] = useState<Market["status"]>("draft");

  const create = useMutation({
    mutationFn: (payload: {
      name: string;
      state: string;
      msa?: string;
      loanBandMinUsd: number;
      loanBandMaxUsd: number;
      termMonthsMin: number;
      termMonthsMax: number;
      status: Market["status"];
    }) =>
      apiFetch<{ id: string }>("/api/settings/markets", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: async ({ id }) => {
      await qc.invalidateQueries({ queryKey: ["settings", "markets"] });
      await navigate({ to: "/settings/markets/$id", params: { id } });
    },
  });

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "Markets" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="text-red-400">Failed to load markets.</p>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6 border-b border-rule pb-5">
        <div>
          <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Markets</h1>
          <p className="text-sm text-muted">
            Manage the market definitions used by candidate discovery. Open a market to edit
            geography, loan band, and loan types.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="rounded bg-gold px-4 py-2 text-sm font-semibold text-ink"
        >
          {showForm ? "Cancel" : "Add market"}
        </button>
      </div>

      {showForm ? (
        <form
          className="grid grid-cols-2 gap-2 rounded border border-rule-strong bg-surface p-4 text-sm md:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || state.trim().length !== 2) return;
            create.mutate({
              name: name.trim(),
              state: state.trim().toUpperCase(),
              msa: msa.trim() || undefined,
              loanBandMinUsd: Number(loanMin),
              loanBandMaxUsd: Number(loanMax),
              termMonthsMin: Number(termMin),
              termMonthsMax: Number(termMax),
              status,
            });
          }}
        >
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-xs text-muted">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">State</span>
            <input
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
              className="rounded border border-rule-strong bg-surface px-3 py-2 uppercase"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">MSA (optional)</span>
            <input
              value={msa}
              onChange={(e) => setMsa(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Loan min ($)</span>
            <input
              value={loanMin}
              onChange={(e) => setLoanMin(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Loan max ($)</span>
            <input
              value={loanMax}
              onChange={(e) => setLoanMax(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Term min (mo)</span>
            <input
              value={termMin}
              onChange={(e) => setTermMin(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Term max (mo)</span>
            <input
              value={termMax}
              onChange={(e) => setTermMax(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as Market["status"])}
              className="rounded border border-rule-strong bg-surface px-3 py-2"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={create.isPending}
            className="col-span-2 rounded bg-gold px-4 py-2 font-semibold text-ink disabled:opacity-50 md:col-span-4"
          >
            {create.isPending ? "Creating…" : "Create market"}
          </button>
        </form>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-rule text-left text-xs uppercase text-muted">
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">State</th>
              <th className="py-2 pr-3">MSA</th>
              <th className="py-2 pr-3">Loan band</th>
              <th className="py-2 pr-3">Term band</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {data.map((m) => (
              <tr key={m.id}>
                <td className="py-2 pr-3 font-medium">{m.name}</td>
                <td className="py-2 pr-3">{m.state}</td>
                <td className="py-2 pr-3 text-muted">{m.msa ?? "—"}</td>
                <td className="py-2 pr-3 text-ink">
                  ${Math.round(m.loanBandMinUsd / 1000)}k–${Math.round(m.loanBandMaxUsd / 1000)}k
                </td>
                <td className="py-2 pr-3 text-ink">
                  {m.termMonthsMin}–{m.termMonthsMax} mo
                </td>
                <td className="py-2 pr-3">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(m.status)}`}
                  >
                    {m.status}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right">
                  <Link
                    to="/settings/markets/$id"
                    params={{ id: m.id }}
                    className="rounded border border-rule-strong bg-surface px-3 py-1 text-xs text-ink hover:bg-paper-2"
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
            {data.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-6 text-center text-muted">
                  No markets yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
